/**
 * /perfil — settings shell with a vertical nav and a content area.
 *
 * Each section is a sibling component imported from ./sections/*. The
 * shell handles tab routing, scroll restoration on tab switch, and
 * shows a skeleton while the profile bootstrap is loading.
 *
 * URL deep-link: la pestaña activa se sincroniza con `?tab=<id>` para
 * que el hub /account (cards) o cualquier link externo aterricen
 * directo en la sección correcta. Cambiar de tab vía nav reescribe el
 * search param sin recargar (replace=true para no inflar el history).
 */

import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  AlertTriangle,
  Building2,
  FileLock2,
  Globe,
  KeyRound,
  Link as LinkIcon,
  Monitor,
  ShieldCheck,
  User as UserIcon,
} from 'lucide-react'

import { useProfile } from '@/hooks/api/use-profile'
import { ProfileDataSection } from './sections/data'
import { PreferencesSection } from './sections/preferences'
import { PasswordSection } from './sections/password'
import { MfaSection } from './sections/mfa'
import { SessionsSection } from './sections/sessions'
import { OAuthSection } from './sections/oauth'
import { OrganizationsSection } from './sections/organizations'
import { PrivacySection } from './sections/privacy'
import { DangerZoneSection } from './sections/danger'

type TabId =
  | 'data'
  | 'organizations'
  | 'preferences'
  | 'password'
  | 'mfa'
  | 'sessions'
  | 'oauth'
  | 'privacy'
  | 'danger'

const TABS: { id: TabId; label: string; icon: typeof UserIcon }[] = [
  { id: 'data', label: 'Datos personales', icon: UserIcon },
  { id: 'organizations', label: 'Mis empresas', icon: Building2 },
  { id: 'preferences', label: 'Preferencias', icon: Globe },
  { id: 'password', label: 'Contraseña', icon: KeyRound },
  { id: 'mfa', label: 'Autenticación 2FA', icon: ShieldCheck },
  { id: 'sessions', label: 'Sesiones activas', icon: Monitor },
  { id: 'oauth', label: 'Cuentas conectadas', icon: LinkIcon },
  { id: 'privacy', label: 'Privacidad y datos', icon: FileLock2 },
  { id: 'danger', label: 'Zona de peligro', icon: AlertTriangle },
]

const VALID_TABS: readonly TabId[] = TABS.map((t) => t.id)

function isTabId(value: string | null): value is TabId {
  return value !== null && (VALID_TABS as readonly string[]).includes(value)
}

export function ProfilePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialTab: TabId = isTabId(searchParams.get('tab'))
    ? (searchParams.get('tab') as TabId)
    : 'data'
  const [tab, setTab] = useState<TabId>(initialTab)
  const { data: profile, isLoading } = useProfile()

  // Reacciona al cambio de ?tab=... en el URL (botón atrás del browser,
  // navegación interna desde el hub /account, etc.). Si el URL trae un
  // tab válido distinto al actual, sincroniza el state.
  useEffect(() => {
    const fromUrl = searchParams.get('tab')
    if (isTabId(fromUrl) && fromUrl !== tab) {
      setTab(fromUrl)
    }
  }, [searchParams, tab])

  // Cuando el usuario cambia de tab vía nav, reflejarlo en el URL para
  // mantener deep-links funcionando + permitir compartir URL. replace
  // (no push) para no llenar el history con cada click de pestaña.
  const handleTabChange = (next: TabId) => {
    setTab(next)
    const params = new URLSearchParams(searchParams)
    if (next === 'data') {
      params.delete('tab') // tab por defecto → URL limpio
    } else {
      params.set('tab', next)
    }
    setSearchParams(params, { replace: true })
  }

  if (isLoading || !profile) {
    return (
      <div className="mx-auto max-w-5xl">
        <div className="h-6 w-48 animate-pulse rounded bg-bg-elevated" />
        <div className="mt-2 h-4 w-72 animate-pulse rounded bg-bg-elevated" />
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[220px,1fr]">
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-9 animate-pulse rounded-md bg-bg-elevated" />
            ))}
          </div>
          <div className="h-72 animate-pulse rounded-xl bg-bg-elevated" />
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl">
      {/* Header con avatar + identidad */}
      <div className="flex items-start gap-4">
        <div
          aria-hidden
          className="flex h-16 w-16 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-rose-500 font-mono text-xl font-bold text-white shadow-sm"
        >
          {profile.initials}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-2xl font-bold text-fg-primary">
            {profile.full_name}
          </h1>
          <p className="truncate text-sm text-fg-secondary">{profile.email}</p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Pill
              variant={profile.email_verified ? 'success' : 'warning'}
              label={profile.email_verified ? 'Correo verificado' : 'Correo sin verificar'}
            />
            <Pill
              variant={profile.totp_enabled ? 'success' : 'warning'}
              label={profile.totp_enabled ? 'MFA activo' : 'MFA desactivado'}
            />
            <Pill
              variant="info"
              label={`Nivel ${profile.verification_level}`}
            />
            {!profile.has_password && (
              <Pill variant="info" label="Solo OAuth (sin contraseña)" />
            )}
          </div>
        </div>
      </div>

      {/* Layout de dos columnas: nav + contenido */}
      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[220px,1fr]">
        <nav className="space-y-1" aria-label="Secciones de configuración">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => handleTabChange(id)}
              aria-current={tab === id ? 'page' : undefined}
              className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${
                tab === id
                  ? 'bg-bg-elevated font-medium text-fg-primary'
                  : 'text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary'
              }`}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{label}</span>
            </button>
          ))}
        </nav>

        <div className="space-y-6">
          {tab === 'data' && <ProfileDataSection profile={profile} />}
          {tab === 'organizations' && <OrganizationsSection />}
          {tab === 'preferences' && <PreferencesSection profile={profile} />}
          {tab === 'password' && <PasswordSection profile={profile} />}
          {tab === 'mfa' && <MfaSection profile={profile} />}
          {tab === 'sessions' && <SessionsSection />}
          {tab === 'oauth' && <OAuthSection profile={profile} />}
          {tab === 'privacy' && <PrivacySection />}
          {tab === 'danger' && <DangerZoneSection profile={profile} />}
        </div>
      </div>
    </div>
  )
}

function Pill({
  variant,
  label,
}: {
  variant: 'success' | 'warning' | 'error' | 'info'
  label: string
}) {
  const cls = {
    success: 'bg-success-tint text-success-fg',
    warning: 'bg-warning-tint text-warning-fg',
    error: 'bg-error-tint text-error-fg',
    info: 'bg-bg-elevated text-fg-secondary',
  }[variant]
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 ${cls}`}>
      {label}
    </span>
  )
}
